import { useEffect, useRef, useState } from 'react'

import {
  EVENT_KEY_ARROW_DOWN,
  EVENT_KEY_ARROW_UP,
  EVENT_KEY_ENTER,
  EVENT_NAME_MOUSE_MOVE,
} from '../../../../constants/event'
import { useKeyboardListener } from '../../../../hooks'

import {
  getFocusedListItemNode,
  getHoveredListItemNodeIndex,
  getKeyDownArrowDownNextIndex,
  getKeyDownArrowUpNextIndex,
  getNullOptionsIndexes,
} from './useListKeyboardNavigation.utils'

// #INFO `options` can be async and static array. But `null` options can be only in static array, cause it's divider element.
export function useListKeyboardNavigation({
  initialFocusIndex,
  listRef,
  options,
  optionsCount,
}) {
  const [focusedItemIndex, setFocusedItemIndex] = useState(initialFocusIndex)
  const [isKeyboardMode, setKeyboardMode] = useState(false)

  const focusedItemIndexRef = useRef(initialFocusIndex)
  const isKeyboardModeRef = useRef(false)

  const nullOptionsIndexes = useRef(getNullOptionsIndexes(options))

  function getHandleMouseEnter(index) {
    return () => {
      if (!isKeyboardModeRef.current) {
        focusedItemIndexRef.current = index
        setFocusedItemIndex(index)
      }
    }
  }

  function handleMouseLeave() {
    if (!isKeyboardModeRef.current) {
      focusedItemIndexRef.current = null
      setFocusedItemIndex(null)
    }
  }

  function handleKeyDownEnter() {
    getFocusedListItemNode(focusedItemIndexRef.current, listRef)?.click()
  }

  function handleKeyDownArrowUp() {
    isKeyboardModeRef.current = true
    setKeyboardMode(true)

    setFocusedItemIndex(prevIndex => {
      const nextIndex = getKeyDownArrowUpNextIndex({
        nullOptionsIndexes: nullOptionsIndexes.current,
        prevIndex,
      })

      focusedItemIndexRef.current = nextIndex

      return nextIndex
    })
  }

  function handleKeyDownArrowDown() {
    isKeyboardModeRef.current = true
    setKeyboardMode(true)

    setFocusedItemIndex(prevIndex => {
      const nextIndex = getKeyDownArrowDownNextIndex({
        nullOptionsIndexes: nullOptionsIndexes.current,
        optionsCount,
        prevIndex,
      })

      focusedItemIndexRef.current = nextIndex

      return nextIndex
    })
  }

  function handleSetMouseMode(event) {
    const hoveredListItemIndex = getHoveredListItemNodeIndex(
      event.target,
      listRef
    )

    isKeyboardModeRef.current = false
    setKeyboardMode(false)

    if (Number.isFinite(hoveredListItemIndex)) {
      focusedItemIndexRef.current = hoveredListItemIndex
      setFocusedItemIndex(hoveredListItemIndex)
    }
  }

  useEffect(() => {
    if (isKeyboardMode) {
      window.addEventListener(EVENT_NAME_MOUSE_MOVE, handleSetMouseMode, {
        once: true,
      })
    }

    return () => {
      window.removeEventListener(EVENT_NAME_MOUSE_MOVE, handleSetMouseMode, {
        once: true,
      })
    }
  }, [isKeyboardMode])

  useEffect(() => {
    if (focusedItemIndex && isKeyboardMode) {
      getFocusedListItemNode(focusedItemIndex, listRef)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [focusedItemIndex])

  useKeyboardListener({
    callback: handleKeyDownArrowDown,
    eventKey: EVENT_KEY_ARROW_DOWN,
  })
  useKeyboardListener({
    callback: handleKeyDownArrowUp,
    eventKey: EVENT_KEY_ARROW_UP,
  })
  useKeyboardListener({
    callback: handleKeyDownEnter,
    eventKey: EVENT_KEY_ENTER,
  })

  return { focusedItemIndex, getHandleMouseEnter, handleMouseLeave }
}
